import { ISendXapiStatement } from '@vedalib/courseAnalitycs'
import { entityAnalyticsConfig } from '@vedalib/courseAnalitycs/src/config'
import { useCallback } from 'react'

import { sendCourseAnalytics } from 'services/Store/Project/actions'
import { useProjectContext, useProjectDispatch } from 'services/Store/Project/hooks'
import { getProject, getProgress, getSection, getSectionId } from 'services/Store/Project/selectors'

import { useGetElementLink } from './useGetElementLink'

type IElementTypes = keyof typeof entityAnalyticsConfig

interface IUseGetEntityAnalyticsConfig<
  T extends IElementTypes,
  K extends (keyof (typeof entityAnalyticsConfig)[T])[],
  E extends string | undefined = string | undefined,
> {
  blockId: string
  elementType: T
  elementTriggers?: K
  elementId?: E
}

export const useGetEntityAnalyticsConfig = <
  T extends IElementTypes,
  K extends (keyof (typeof entityAnalyticsConfig)[T])[],
  E extends string | undefined = string | undefined,
>({
  blockId,
  elementType,
  elementTriggers,
  elementId = '',
}: IUseGetEntityAnalyticsConfig<T, K, E>) => {
  const project = useProjectContext(getProject)
  const projectId = project?.id || ''
  const companyId = project?.companyId || ''
  const coverId = useProjectContext(getSection, { cover: true })?.id || ''
  const sectionId = useProjectContext(getSectionId) || coverId
  const progress = useProjectContext(getProgress) ?? 0
  const elementUrl = useGetElementLink(blockId)
  const dispatch = useProjectDispatch()
  const sendXapiData = useCallback(
    (data: ISendXapiStatement) => dispatch(sendCourseAnalytics(data)),
    [dispatch],
  )

  const baseObjectExtensions = {
    elementUrl,
    blockId,
    sectionId,
    elementId,
    companyId,
    projectId,
    progress,
  }

  return {
    entityAnalyticsConfig: entityAnalyticsConfig[elementType],
    elementTriggers: elementTriggers?.length
      ? elementTriggers.reduce<Record<K[number], (typeof entityAnalyticsConfig)[T][K[number]]>>(
          (acc, t) => ({
            ...acc,
            t: entityAnalyticsConfig[elementType][t],
          }),
          {} as Record<K[number], (typeof entityAnalyticsConfig)[T][K[number]]>,
        )
      : null,
    elementUrl,
    baseObjectExtensions,
    sendXapiData,
  }
}
